import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  getToken,
  setToken,
  getRefreshToken,
  setRefreshToken,
  clearAuth,
} from "./storage";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const tokenHeaders = () => {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

// Utility function to handle logout when token expires
export const handleLogout = () => {
  clearAuth();
  // Redirect to login page
  window.location.href = "/login";
};

export const authedFetch = async (url, options = {}, params = {}) => {
  let resp = await fetch(url, {
    ...options,
    headers: { ...tokenHeaders(), ...(options.headers || {}) },
  });

  // Check for 401 Unauthorized - token expired or invalid
  if (resp.status === 401) {
    // Don't try to refresh on auth endpoints
    if (
      url.includes("/api/v1/auth") ||
      url.includes("/api/v1/auth-enhanced") ||
      url.includes("/refresh")
    ) {
      handleLogout();
      throw new Error("Token expired");
    }

    // Try to refresh the token before logging out
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        const refreshUrl = buildApiUrl("/api/v1/auth/refresh");
        // FastAPI expects refresh_token in the request body
        // Try JSON format first (most common for FastAPI)
        const refreshResp = await fetch(refreshUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResp.ok) {
          const refreshData = await refreshResp.json();
          if (refreshData?.access_token) {
            setToken(refreshData.access_token);
            if (refreshData.refresh_token) {
              setRefreshToken(refreshData.refresh_token);
            }

            // Retry the original request with new token
            resp = await fetch(url, {
              ...options,
              headers: { ...tokenHeaders(), ...(options.headers || {}) },
            });

            if (resp.status === 401) {
              handleLogout();
              throw new Error("Token expired");
            }
          } else {
            handleLogout();
            throw new Error("Token expired");
          }
        } else {
          handleLogout();
          throw new Error("Token expired");
        }
      } catch (refreshError) {
        handleLogout();
        throw new Error("Token expired");
      }
    } else {
      handleLogout();
      throw new Error("Token expired");
    }
  }

  if (!resp.ok) throw new Error((await resp.text()) || "Request failed");
  return resp.json();
};

// Helper to build API URLs - use relative URLs for proxy, or full URL if backendUrl is set
export const buildApiUrl = (path) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (backendUrl) {
    return `${backendUrl}${path.startsWith("/") ? path : `/${path}`}`;
  }
  return path.startsWith("/") ? path : `/${path}`;
};

export const createUrlQueryString = (params) => {
  const searchParams = new URLSearchParams();

  // Iterate over the keys and values of the parameters object
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      let value = params[key];

      // If the value is an array, serialize it to a JSON string
      if (Array.isArray(value)) {
        value = JSON.stringify(value);
      }

      // Append the key-value pair to URLSearchParams
      // URLSearchParams automatically handles URL encoding
      searchParams.append(key, value);
    }
  }

  return searchParams.toString();
};

export const getSocketUrl = (url) => {
  return url.replace("http", "ws");
};

export const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount);

export const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";

export const toTitleCase = (str) => {
  const exceptions = [
    "a",
    "an",
    "the",
    "and",
    "but",
    "or",
    "for",
    "nor",
    "on",
    "at",
    "to",
    "by",
    "of",
  ];
  return str
    .toLowerCase()
    .split(" ")
    .map((word, index) => {
      if (index > 0 && exceptions.includes(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};
